import Card from '@rio-cloud/rio-uikit/Card';
import Icon from '@rio-cloud/rio-uikit/Icon';
import { useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';

import type { RootState } from '../../../configuration/setup/store';
import { PaymentMethodPageContainer } from '../common/payment/PaymentMethodPageContainer';
import { getPaymentDetailsIsFetched } from '../common/payment/redux/paymentMethodsSelectionService';
import { safeDataLayerPush } from '../common/utils/googleTagManagerWrapper';
import { isPurchaseFree, usePaymentMethodRequirement } from './CheckoutService';
import {
    CheckoutSteps,
    type GACheckoutStepData,
    getGACheckoutStepData,
    getGAEventCheckoutProgress,
} from './checkoutGTMUtils';
import { CheckoutStepHeader } from './components/CheckoutStepHeader';
import { NextButton, PreviousButton } from './components/NavigationButtons';
import { useRedirectToResourceSelection } from './deeplinking/CheckoutRouteHelper';

interface Props {
    purchaseFree: boolean;
    enableNextStep: boolean;
    nextPath: string;
    previousPath: string;
    gAData: GACheckoutStepData;
}

const CheckoutPaymentPage = (props: Props) => {
    const { purchaseFree, nextPath, previousPath, enableNextStep, gAData } = props;
    useRedirectToResourceSelection();
    usePaymentMethodRequirement();

    useEffect(() => {
        if (gAData.product) {
            safeDataLayerPush(() => getGAEventCheckoutProgress(gAData));
        }
    }, [gAData.product]);

    const content = purchaseFree ? (
        <div className='display-flex flex-column align-items-center gap-10 padding-y-25 text-center'>
            <Icon iconName='check-shield' size='h2' iconClassName='text-color-success' />
            <div className='text-bold'>
                <FormattedMessage id='marketplace.payment.information.free' />
            </div>
        </div>
    ) : (
        <PaymentMethodPageContainer />
    );

    return (
        <section className='padding-bottom-25'>
            <CheckoutStepHeader title={<FormattedMessage id='marketplace.payment.headline' />} />
            <Card className='CheckoutPaymentPage' padding={25} shadow='subtle'>
                {content}
                <hr className='margin-y-20' />
                <div className='display-flex justify-content-between flex-wrap gap-10'>
                    <PreviousButton previousPath={previousPath} />
                    <NextButton nextPath={nextPath} disabled={!enableNextStep} />
                </div>
            </Card>
        </section>
    );
};

export const mapStateToProps = (state: RootState) => ({
    purchaseFree: isPurchaseFree(state),
    enableNextStep: isPurchaseFree(state) || getPaymentDetailsIsFetched(state),
    gAData: getGACheckoutStepData(state, CheckoutSteps.PAYMENT_INFORMATION_ENTERED),
});

export const CheckoutPaymentPageContainer = connect(mapStateToProps)(CheckoutPaymentPage);
